import React, { useEffect } from 'react';

import { matchPath } from 'react-router-dom';
import qs from 'qs';

import { routes } from 'constants/config';
import { Heading, HEADING_VARIANTS, Text, TEXT_VARIANTS } from 'ui/Typography';
import Skeleton from 'ui/Skeleton';
import PageTitle from 'components/shared/PageTitle';
import useFetch from 'hooks/useFetch';

import LessonProgressProvider from 'components/LessonProgress/LessonProgressProvider';
import IncompleteSections, {
  IncompleteSectionsSkeleton,
} from './IncompleteSections';

const isLibraryPath = pathname => !!matchPath(pathname, { path: '/library' });

// Handles content in learning library, path & lesson or only lesson
// We do this to generate proper url to link users
// to the their missed questions or incorrect knowledge checks (section.htmlUrl & question.htmlUrl)
export const getMissedUrl = ({ search, pathname }, pathId, lessonId) => {
  const isLearningLibraryPath = isLibraryPath(pathname);
  const locationQuery = qs.parse(search, { ignoreQueryPrefix: true });
  const query = {};
  if (locationQuery.embed === 'true') {
    query.embed = true;
  }

  if (isLearningLibraryPath) {
    return pathId
      ? routes.LIBRARY_PATH_LESSON_MISSED(pathId, lessonId, query)
      : routes.LIBRARY_LESSON_MISSED(lessonId, query);
  }

  return pathId
    ? routes.PATH_LESSON_MISSED(pathId, lessonId, query)
    : routes.LESSON_MISSED(lessonId, query);
};

export default function CompleteMissedContents({
  location,
  match: { params },
}) {
  const { pathId, lessonId } = params;
  const missedUrl = getMissedUrl(location, pathId, lessonId);
  const { loading, error, data = {} } = useFetch(missedUrl);

  // Allow Page Error Boundary handles api errors
  useEffect(() => {
    if (error) {
      throw new Error(error);
    }
  }, [error]);

  const {
    sectionsWithMissedQuestions,
    sectionsWithIncorrectlyAnsweredKnowledgeChecks,
    sectionsWithMissedRolePlays,
    sectionsWithUnfinishedElearnings,
    sectionsWithMissedEventElements,
  } = data;

  return (
    <>
      <PageTitle title={t('questions.user_error.title')} />
      <LessonProgressProvider percentage={100} />
      <article className="missed_questions">
        <header>
          <Heading component="h2" variant={HEADING_VARIANTS.H4}>
            {loading ? (
              <Skeleton wrapper="h2" width="25%" />
            ) : (
              t('questions.user_error.title')
            )}
          </Heading>
        </header>
      </article>
      {loading && <IncompleteSectionsSkeleton />}
      {!loading && (
        <article className="missed_questions">
          <header className="mb-24">
            <Text component="h3" variant={TEXT_VARIANTS.SUBHEADER}>
              {t('questions.user_error.incorrect.instructions')}
            </Text>
          </header>
          <IncompleteSections sections={sectionsWithMissedQuestions} />
          <IncompleteSections
            sections={sectionsWithIncorrectlyAnsweredKnowledgeChecks}
          />
          <IncompleteSections sections={sectionsWithMissedRolePlays} />
          <IncompleteSections sections={sectionsWithUnfinishedElearnings} />
          <IncompleteSections sections={sectionsWithMissedEventElements} />
        </article>
      )}
    </>
  );
}
